<template>
  <div class="modal-overlay" v-if="visible && participant" @click.self="$emit('close')">
    <div class="modal-card details-modal">
      <button class="close-btn" type="button" @click="$emit('close')" aria-label="Fechar">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
      </button>

      <header class="details-header">
        <h2 class="details-name">{{ participant.name }}</h2>
        <EquipeChip :equipe="participant.equipe" />
      </header>

      <div class="status-banner" :class="participant.check ? 'banner-confirmed' : 'banner-pending'">
        <svg v-if="participant.check" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM12 22q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m.5-13H11v6l5.2 3.2l.8-1.3l-4.5-2.7V7Z"/></svg>
        <span>{{ participant.check ? 'Check-in confirmado' : 'Aguardando check-in' }}</span>
      </div>

      <dl class="details-list">
        <div class="details-row">
          <dt>Contato</dt>
          <dd>{{ participant.contato || '—' }}</dd>
        </div>

        <template v-if="participant.check">
          <div class="details-row">
            <dt>Transporte</dt>
            <dd>
              <TransporteBadge v-if="participant.transporte" :value="participant.transporte" />
              <span v-else class="text-muted">—</span>
            </dd>
          </div>

          <div class="details-row">
            <dt>Horário do check-in</dt>
            <dd>{{ formatDateTime(participant.checkedAt) }}</dd>
          </div>

          <div class="details-row">
            <dt>Confirmado por</dt>
            <dd>{{ participant.checkedBy?.name || 'Sem login' }}</dd>
          </div>
        </template>

        <div class="details-row">
          <dt>Cadastrado por</dt>
          <dd>{{ participant.createdBy?.name || '—' }}</dd>
        </div>

        <div class="details-row">
          <dt>Cadastrado em</dt>
          <dd>{{ formatDateTime(participant.createdAt) }}</dd>
        </div>

        <div class="details-row">
          <dt>Participação</dt>
          <dd>
            <span v-if="!isDiaria(participant)" class="participacao-pill participacao-completo">Completo</span>
            <span v-else class="participacao-row">
              <span class="participacao-pill participacao-diaria">📅 Diária</span>
              <span v-for="d in diasLongChips(participant.diasAcamp)" :key="d" class="dia-chip">{{ d }}</span>
            </span>
          </dd>
        </div>
      </dl>

      <!-- Seção de ficha de inscrição -->
      <section class="ficha-section">
        <header class="section-header">
          <h3 class="section-title">Ficha de inscrição</h3>
          <span v-if="participant.fichaPreenchidaEm" class="ficha-pill ficha-pill-ok">
            <i class="fa fa-clipboard-check"></i> Completa
          </span>
          <span v-else class="ficha-pill ficha-pill-falta">
            <i class="fa fa-clipboard-list"></i> Falta preencher
          </span>
        </header>

        <div v-if="participant.fichaPreenchidaEm" class="ficha-resumo">
          <div class="ficha-row"><span>Sexo</span><strong>{{ sexoLabel(participant.sexo) || '—' }}</strong></div>
          <div class="ficha-row"><span>Acomodação</span><strong>{{ acomodacaoLabel(participant.acomodacao) || '—' }}</strong></div>
          <div class="ficha-row"><span>Transporte ida</span><strong>{{ transporteLabel(participant.transporte) || '—' }}</strong></div>
          <div class="ficha-row"><span>Alergias</span><strong>{{ participant.alergias || '—' }}</strong></div>
          <div class="ficha-row"><span>Restrição alimentar</span><strong>{{ participant.restricaoAlimentar || '—' }}</strong></div>
          <div class="ficha-row" v-if="participant.medicamentos"><span>Medicamentos</span><strong>{{ participant.medicamentos }}</strong></div>
          <div class="ficha-row" v-if="participant.condicoesSaude"><span>Condições</span><strong>{{ participant.condicoesSaude }}</strong></div>
          <div class="ficha-row" v-if="participant.responsavelNome">
            <span>Responsável</span>
            <strong>{{ participant.responsavelNome }} · {{ participant.responsavelTelefone }} ({{ participant.responsavelParentesco }})</strong>
          </div>
          <div class="ficha-row">
            <span>Emergência</span>
            <strong>{{ participant.emergenciaNome }} · {{ participant.emergenciaTelefone }} ({{ participant.emergenciaParentesco }})</strong>
          </div>
          <div class="ficha-row"><span>Termos</span><strong>{{ participant.aceitouTermos ? '✓ Aceitos' : '— Não aceitos' }}</strong></div>
          <div class="ficha-row"><span>Preenchida em</span><strong>{{ formatDateTime(participant.fichaPreenchidaEm) }}</strong></div>
        </div>
        <p v-else class="ficha-empty">
          O acampante ainda não preencheu a ficha. Envie o link pra ele via WhatsApp.
        </p>

        <div class="ficha-actions">
          <button class="btn-secondary" @click="copyInscricaoLink">
            <i class="fa fa-link"></i>
            <span>{{ copyTooltip || 'Copiar link' }}</span>
          </button>
          <a
            v-if="!participant.fichaPreenchidaEm && conviteWaLink"
            class="btn-secondary enviar-wpp"
            :href="conviteWaLink"
            target="_blank"
            rel="noopener"
            title="Enviar convite de preenchimento via WhatsApp"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488a11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592c5.448 0 9.886-4.434 9.889-9.885c.002-5.462-4.415-9.89-9.881-9.892c-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.667 5.39l-.999 3.648zM12.05 5.97c-.34-.011-.66-.011-.971.062c-.32.075-.71.273-1.072.638c-.371.366-1.402 1.37-1.402 3.345s1.435 3.881 1.635 4.146c.2.264 2.79 4.267 6.844 5.978c.961.413 1.722.66 2.317.842c.985.314 1.881.27 2.591.165c.79-.117 2.448-1.002 2.79-1.97c.343-.97.343-1.797.24-1.973c-.103-.175-.376-.279-.785-.487c-.41-.207-2.42-1.197-2.795-1.334c-.376-.137-.65-.205-.923.207c-.275.412-1.06 1.334-1.299 1.609c-.24.275-.479.309-.888.103c-.41-.207-1.732-.638-3.297-2.035c-1.219-1.085-2.04-2.422-2.279-2.832c-.24-.412-.024-.635.184-.84c.184-.184.41-.479.617-.718c.207-.24.276-.412.412-.687c.137-.275.07-.514-.034-.722c-.103-.207-.918-2.214-1.293-3.026c-.349-.755-.694-.638-.923-.638z"/></svg>
            <span>Enviar via WhatsApp</span>
          </a>
          <button v-if="!participant.fichaPreenchidaEm" class="btn-secondary" @click="copyMensagemConvite">
            <i class="fa fa-clone"></i>
            <span>{{ copyConviteTooltip || 'Copiar mensagem' }}</span>
          </button>
          <button class="btn-secondary" @click="$emit('open-ficha')">
            <i class="fa fa-clipboard"></i>
            <span>{{ participant.fichaPreenchidaEm ? 'Editar ficha' : 'Abrir ficha' }}</span>
          </button>
        </div>
      </section>

      <!-- Seção financeira (oculta para CHECKIN) -->
      <section v-if="canSeeFinanceiro" class="financeiro-section">
        <header class="section-header">
          <h3 class="section-title">Financeiro</h3>
          <span v-if="statusFin === 'quitado'" class="fin-pill fin-quitado">✓ Quitado</span>
          <span v-else-if="statusFin === 'negociado'" class="fin-pill fin-negociado">🤝 Negociado</span>
          <span v-else-if="statusFin === 'devendo'" class="fin-pill fin-devendo">Falta {{ formatBRL(participant.falta) }}</span>
          <span v-else class="fin-pill fin-sem-total">Sem total definido</span>
        </header>

        <p v-if="statusFin === 'negociado' && participant.observacaoNegociacao" class="negociacao-obs">
          <strong>Observação do acordo:</strong> {{ participant.observacaoNegociacao }}
        </p>

        <!-- Toggle inline de negociacao (ROOT/ADMIN) -->
        <div v-if="canManageAcampantes" class="negociacao-toggle">
          <label class="negociacao-label">
            <input type="checkbox" :checked="!!participant.negociado" :disabled="savingNegociado" @change="onNegociadoToggle($event.target.checked)" />
            <span>🤝 Acordo de pagamento (negociado)</span>
          </label>
          <textarea
            v-if="participant.negociado"
            class="input negociacao-input"
            :value="participant.observacaoNegociacao || ''"
            :disabled="savingNegociado"
            placeholder="Observação do acordo (data prevista, condição...)"
            maxlength="300"
            rows="2"
            @blur="onObservacaoBlur($event.target.value)"
          ></textarea>
          <span v-if="negociadoMsg" class="negociacao-hint">{{ negociadoMsg }}</span>
        </div>

        <div v-if="participant.valorTotal != null" class="fin-summary">
          <div class="fin-cell">
            <span class="fin-label">Total</span>
            <span class="fin-value">{{ formatBRL(participant.valorTotal) }}</span>
          </div>
          <div class="fin-cell">
            <span class="fin-label">Pago</span>
            <span class="fin-value pago">{{ formatBRL(participant.valorPago) }}</span>
          </div>
          <div class="fin-cell">
            <span class="fin-label">Falta</span>
            <span class="fin-value" :class="participant.falta > 0 ? 'falta' : 'pago'">{{ formatBRL(participant.falta) }}</span>
          </div>
        </div>

        <div v-if="participant.pagamentos && participant.pagamentos.length > 0" class="pagamentos-list">
          <div class="pagamento-item" v-for="p in participant.pagamentos" :key="p.id">
            <span class="pag-data">{{ formatDate(p.data) }}</span>
            <span class="pag-valor">{{ formatBRL(p.valor) }}</span>
            <span class="pag-obs" v-if="p.observacao">{{ p.observacao }}</span>
            <span v-else class="pag-obs muted">—</span>
            <button class="pag-del" @click="$emit('delete-pagamento', p)" :title="`Remover pagamento de ${formatBRL(p.valor)}`">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
            </button>
          </div>
        </div>
        <p v-else class="fin-empty">Sem pagamentos registrados.</p>

        <div class="fin-actions">
          <button class="btn-secondary" @click="$emit('add-pagamento')">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Registrar pagamento
          </button>
          <template v-if="statusFin === 'devendo'">
            <a
              v-if="whatsappLink"
              class="btn-secondary cobrar-wpp"
              :href="whatsappLink"
              target="_blank"
              rel="noopener"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488a11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592c5.448 0 9.886-4.434 9.889-9.885c.002-5.462-4.415-9.89-9.881-9.892c-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.667 5.39l-.999 3.648zM12.05 5.97c-.34-.011-.66-.011-.971.062c-.32.075-.71.273-1.072.638c-.371.366-1.402 1.37-1.402 3.345s1.435 3.881 1.635 4.146c.2.264 2.79 4.267 6.844 5.978c.961.413 1.722.66 2.317.842c.985.314 1.881.27 2.591.165c.79-.117 2.448-1.002 2.79-1.97c.343-.97.343-1.797.24-1.973c-.103-.175-.376-.279-.785-.487c-.41-.207-2.42-1.197-2.795-1.334c-.376-.137-.65-.205-.923.207c-.275.412-1.06 1.334-1.299 1.609c-.24.275-.479.309-.888.103c-.41-.207-1.732-.638-3.297-2.035c-1.219-1.085-2.04-2.422-2.279-2.832c-.24-.412-.024-.635.184-.84c.184-.184.41-.479.617-.718c.207-.24.276-.412.412-.687c.137-.275.07-.514-.034-.722c-.103-.207-.918-2.214-1.293-3.026c-.349-.755-.694-.638-.923-.638z"/></svg>
              Cobrar via WhatsApp
            </a>
            <button class="btn-secondary" @click="copyCobranca">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
              Copiar mensagem
            </button>
          </template>
        </div>
      </section>

      <div class="modal-actions">
        <button class="btn-secondary" @click="$emit('close')">Fechar</button>
        <button class="btn-secondary" @click="$emit('qr')">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M3 11h8V3H3v8zm2-6h4v4H5V5zm-2 16h8v-8H3v8zm2-6h4v4H5v-4zm8-12v8h8V3h-8zm6 6h-4V5h4v4zm0 4h-2v2h2v-2zM11 13H9v4h2v-2h2v-2h-2v0zm6 0h-2v2h2v-2zm-2 4h-2v2h2v-2zm2-2h2v-2h-2v2zm0 6h2v-4h-2v4z"/></svg>
          QR Code
        </button>
        <button v-if="canManageAcampantes" class="btn-primary" @click="$emit('edit')">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83l3.75 3.75l1.83-1.83z"/></svg>
          Editar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import EquipeChip from './EquipeChip.vue'
import TransporteBadge from './TransporteBadge.vue'
import { formatBRL, statusFinanceiro, buildMensagemCobranca, buildWhatsAppLink } from '../lib/financeiro'
import { buildPublicLink, buildConviteWhatsAppLink, buildMensagemConvite } from '../lib/inscricao'
import { sexoLabel } from '../lib/sexos'
import { acomodacaoLabel } from '../lib/acomodacoes'
import { getTransporte } from '../lib/transportes'
import { isDiaria, diasLongChips } from '../lib/dias'
import { useAuthStore } from '../stores/auth'
import { useAcampantesStore } from '../stores/acampantes'

export default {
  name: 'AcampanteDetailsModal',
  components: { EquipeChip, TransporteBadge },
  props: {
    visible: { type: Boolean, default: false },
    participant: { type: Object, default: null }
  },
  emits: ['close', 'edit', 'qr', 'add-pagamento', 'delete-pagamento', 'open-ficha'],
  data() {
    return { copyTooltip: '', copyConviteTooltip: '', savingNegociado: false, negociadoMsg: '' }
  },
  computed: {
    statusFin() { return statusFinanceiro(this.participant) },
    whatsappLink() { return buildWhatsAppLink(this.participant) },
    conviteWaLink() { return this.participant ? buildConviteWhatsAppLink(this.participant) : null },
    authStore() { return useAuthStore() },
    canSeeFinanceiro() { return this.authStore.canSeeFinanceiro },
    canManageAcampantes() { return this.authStore.canManageAcampantes }
  },
  methods: {
    formatBRL,
    sexoLabel,
    acomodacaoLabel,
    isDiaria,
    diasLongChips,
    transporteLabel(value) { return getTransporte(value)?.label || null },
    async saveNegociacao(payload) {
      if (this.savingNegociado || !this.participant) return
      this.savingNegociado = true
      this.negociadoMsg = ''
      try {
        await useAcampantesStore().update(this.participant.id, payload)
        this.negociadoMsg = 'Salvo!'
        setTimeout(() => { this.negociadoMsg = '' }, 2000)
      } catch (err) {
        this.negociadoMsg = err?.response?.data?.message || 'Falha ao salvar'
        setTimeout(() => { this.negociadoMsg = '' }, 3000)
      } finally {
        this.savingNegociado = false
      }
    },
    onNegociadoToggle(checked) {
      const payload = { negociado: checked }
      if (!checked) payload.observacaoNegociacao = null
      this.saveNegociacao(payload)
    },
    onObservacaoBlur(value) {
      if (!this.participant?.negociado) return
      const trimmed = (value || '').trim()
      if (trimmed === (this.participant.observacaoNegociacao || '')) return
      this.saveNegociacao({ observacaoNegociacao: trimmed || null })
    },
    async copyMensagemConvite() {
      const msg = buildMensagemConvite(this.participant)
      try {
        await navigator.clipboard.writeText(msg)
        this.copyConviteTooltip = 'Mensagem copiada!'
      } catch {
        this.copyConviteTooltip = 'Falha ao copiar'
      }
      setTimeout(() => { this.copyConviteTooltip = '' }, 2000)
    },
    async copyInscricaoLink() {
      const url = buildPublicLink(this.participant.id)
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          const ta = document.createElement('textarea')
          ta.value = url
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copyTooltip = 'Link copiado!'
        setTimeout(() => { this.copyTooltip = '' }, 2000)
      } catch {
        this.copyTooltip = 'Falha ao copiar'
        setTimeout(() => { this.copyTooltip = '' }, 2000)
      }
    },
    formatDateTime(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('pt-BR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })
      } catch { return '—' }
    },
    formatDate(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleDateString('pt-BR', {
          day: '2-digit', month: '2-digit', year: '2-digit'
        })
      } catch { return '—' }
    },
    async copyCobranca() {
      const msg = buildMensagemCobranca(this.participant)
      try {
        await navigator.clipboard.writeText(msg)
        this.$toast?.open({ message: 'Mensagem copiada', type: 'success', position: 'top-right', duration: 2500 })
      } catch {
        this.$toast?.open({ message: 'Não foi possível copiar', type: 'error', position: 'top-right', duration: 3000 })
      }
    }
  }
}
</script>

<style scoped>
.details-modal {
  position: relative;
  max-width: 480px;
  padding: var(--spacing-lg);
}
.close-btn {
  position: absolute;
  top: 12px; right: 12px;
  width: 32px; height: 32px;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; cursor: pointer;
  color: var(--color-text-muted);
  border-radius: var(--radius-circle);
  transition: var(--transition-fast);
}
.close-btn:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }

.details-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  padding-right: var(--spacing-lg);
  margin-bottom: var(--spacing-base);
}
.details-name {
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--color-text-primary);
  margin: 0;
  word-break: break-word;
}

.status-banner {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 8px 14px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  margin-bottom: var(--spacing-base);
}
.banner-confirmed {
  background: rgba(46, 204, 113, 0.12);
  color: var(--color-success);
}
.banner-pending {
  background: rgba(243, 156, 18, 0.12);
  color: var(--color-warning);
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin: 0 0 var(--spacing-lg);
}
.details-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--color-bg-separator);
}
.details-row:last-child { border-bottom: none; }
.details-row dt {
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--color-text-muted);
}
.details-row dd {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-primary);
  word-break: break-word;
}
.text-muted { color: var(--color-text-muted); }

/* Financeiro */
.financeiro-section {
  margin: 0 0 var(--spacing-lg);
  padding-top: var(--spacing-base);
  border-top: 1px solid var(--color-bg-separator);
}
.section-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: var(--spacing-sm); flex-wrap: wrap;
  margin-bottom: var(--spacing-md);
}
.section-title {
  font-size: var(--text-md); font-weight: 800;
  color: var(--color-text-primary); margin: 0;
  letter-spacing: 0.3px;
}
.fin-pill {
  font-size: 11px; font-weight: 800;
  padding: 4px 10px; border-radius: 12px;
}
.fin-quitado { background: rgba(46, 204, 113, 0.15); color: var(--color-success); }
.fin-devendo { background: rgba(231, 76, 60, 0.12); color: var(--color-danger); }
.fin-negociado { background: rgba(245, 158, 11, 0.15); color: #b45309; }
.fin-sem-total { background: var(--color-bg-hover); color: var(--color-text-muted); border: 1px dashed #bbb; }

.negociacao-obs {
  font-size: var(--text-xs);
  background: rgba(245, 158, 11, 0.08);
  color: #92400e;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  border-left: 3px solid #f59e0b;
  margin: 0 0 var(--spacing-base);
}
.negociacao-obs strong { color: #78350f; }

.negociacao-toggle {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  margin: 0 0 var(--spacing-base);
}
.negociacao-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  cursor: pointer;
  user-select: none;
}
.negociacao-label input { width: 16px; height: 16px; cursor: pointer; }
.negociacao-input {
  width: 100%;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  resize: vertical;
  min-height: 48px;
}
.negociacao-hint {
  font-size: var(--text-xs);
  color: var(--color-success);
  font-weight: 700;
}

.fin-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-base);
}
.fin-cell {
  display: flex; flex-direction: column; gap: 2px;
  padding: 10px 12px;
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
}
.fin-label {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: var(--color-text-muted);
}
.fin-value { font-size: var(--text-md); font-weight: 700; color: var(--color-text-primary); }
.fin-value.pago { color: var(--color-success); }
.fin-value.falta { color: var(--color-danger); }

.pagamentos-list {
  display: flex; flex-direction: column; gap: 4px;
  margin-bottom: var(--spacing-md);
}
.pagamento-item {
  display: grid;
  grid-template-columns: 70px 90px 1fr 28px;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 6px 10px;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
}
.pag-data { color: var(--color-text-muted); font-weight: 600; }
.pag-valor { font-weight: 700; color: var(--color-text-primary); }
.pag-obs { color: var(--color-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pag-obs.muted { color: var(--color-text-muted); }
.pag-del {
  width: 22px; height: 22px;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; cursor: pointer;
  color: var(--color-text-muted);
  border-radius: 50%;
}
.pag-del:hover { background: var(--color-danger); color: #fff; }

.fin-empty {
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-align: center; padding: var(--spacing-sm) 0; margin: 0 0 var(--spacing-md);
}
.fin-actions {
  display: flex; gap: var(--spacing-sm); flex-wrap: wrap;
}
.cobrar-wpp { background: #25d366; color: #fff !important; border-color: #25d366; }
.cobrar-wpp:hover { background: #1ebe5d; }

/* Ficha de inscrição */
.ficha-section {
  margin: 0 0 var(--spacing-lg);
  padding-top: var(--spacing-base);
  border-top: 1px solid var(--color-bg-separator);
}
.ficha-pill {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 800;
  padding: 4px 10px; border-radius: 12px;
}
.ficha-pill-ok { background: #e6f4ea; color: #137333; }
.ficha-pill-falta { background: var(--color-bg-hover); color: var(--color-text-muted); }

.ficha-resumo {
  display: flex; flex-direction: column; gap: 4px;
  margin-bottom: var(--spacing-md);
}
.ficha-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: var(--spacing-sm);
  padding: 4px 0;
  font-size: var(--text-xs);
}
.ficha-row span {
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 700;
}
.ficha-row strong { color: var(--color-text-primary); font-weight: 500; word-break: break-word; }

.ficha-empty {
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-align: center; padding: var(--spacing-sm) 0;
  margin: 0 0 var(--spacing-md);
}
.ficha-actions {
  display: flex; gap: var(--spacing-sm); flex-wrap: wrap;
}
.ficha-actions .enviar-wpp { background: #25d366; color: #fff !important; border-color: #25d366; }
.ficha-actions .enviar-wpp:hover { background: #1ebe5d; color: #fff !important; }

.participacao-pill {
  display: inline-flex; align-items: center;
  font-size: 11px; font-weight: 800;
  padding: 3px 10px; border-radius: 12px;
  letter-spacing: 0.3px;
}
.participacao-completo { background: rgba(51, 102, 204, 0.12); color: var(--color-accent); }
.participacao-diaria { background: rgba(25, 118, 210, 0.15); color: #1976d2; }
.participacao-row { display: inline-flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.dia-chip {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 10px;
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
  font-size: 11px; font-weight: 600;
}

.modal-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .details-row { grid-template-columns: 1fr; gap: 4px; }
  .details-row dd { font-size: var(--text-md); }
  .modal-actions { flex-direction: column-reverse; }
  .modal-actions button { width: 100%; justify-content: center; }
  .fin-summary { grid-template-columns: 1fr 1fr; }
  .pagamento-item { grid-template-columns: 64px 80px 1fr 24px; gap: 6px; }
  .fin-actions .btn-secondary { flex: 1; justify-content: center; min-width: 0; }
}
</style>
